import { useSettings } from '@wix/tpa-settings/react';
import { ErrorSmall } from '@wix/wix-ui-icons-common/on-stage';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import React from 'react';
import {
  Dropdown,
  type DropdownOptionProps,
  DropdownOptionTextOverflow,
  DropdownTheme,
  Text,
  TextPriority,
} from 'wix-ui-tpa/cssVars';
import {
  BorderStyle,
  type PaymentOption,
  PaymentType,
} from '../../../../../../types/types';
import { getServiceSlotIdentifier } from '../../../../../../utils';
import settingsParams from '../../../../settingsParams';
import { DiscountRuleName } from '../../../Discount/Discount';
import { ServicePaymentDropdownHooks } from './dataHooks';
import type { PaymentFormatProps } from './ServicePaymentOptions';
import { classes, st } from './ServicePaymentDropdown.st.css';

export const ServicePaymentDropdown: React.FC<PaymentFormatProps> = ({
  service,
  paymentOptions,
  label,
  selectedId,
  onSelect,
  getPaymentOptionLabel,
  buildPaymentOptionId,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();
  const settings = useSettings();
  const theme =
    settings.get(settingsParams.fieldsBorderStyle) === BorderStyle.UNDERLINE
      ? DropdownTheme.Line
      : DropdownTheme.Box;

  const getPaymentSectionTitle = (paymentOption: PaymentOption) => {
    switch (paymentOption.type) {
      case PaymentType.SINGLE_SESSION: {
        return t(
          'app.bookings-form.payment-section.service-dropdown.pay-for-this-session-section-title',
        );
      }
      case PaymentType.BUY_PLAN: {
        return t(
          'app.bookings-form.payment-section.service-dropdown.buy-a-plan-section-title',
        );
      }
      case PaymentType.USE_PLAN: {
        return t(
          'app.bookings-form.payment-section.service-dropdown.use-my-plan-section-title',
        );
      }
    }
  };

  const shouldShowSectionTitle = (currentOption: PaymentOption) => {
    if (currentOption.type === PaymentType.SINGLE_SESSION) {
      return !!paymentOptions.find(
        (o) => o.type !== PaymentType.SINGLE_SESSION,
      );
    } else {
      return true;
    }
  };

  const getPaymentOptionsDropdownOptions = (options: PaymentOption[]) => {
    let lastPaymentOptionType: PaymentType | undefined;
    const results: DropdownOptionProps[] = [];
    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < options.length; i++) {
      const option = options[i];
      if (options.length > 1) {
        if (option.type !== lastPaymentOptionType) {
          if (i !== 0) {
            results.push({ divider: true });
          }
          if (shouldShowSectionTitle(option)) {
            results.push({
              id: `section-${option.type}`,
              value: getPaymentSectionTitle(option),
              textOverflow: DropdownOptionTextOverflow.wrap,
              isSelectable: false,
              isSectionTitle: true,
            });
          }
        }
      }

      const suffixPriority = option.disabled
        ? TextPriority.secondary
        : TextPriority.primary;
      const suffix = option.suffix ? (
        <Text priority={suffixPriority} aria-disabled={option.disabled}>
          {option.suffix}
        </Text>
      ) : undefined;

      results.push({
        id: buildPaymentOptionId(option),
        value: getPaymentOptionLabel(option),
        subtitle: option.discountDetails ? (
          <>
            {option.pricingDetails && <div>{option.pricingDetails}</div>}
            <DiscountRuleName {...option.discountDetails} align="start" />
          </>
        ) : option.disabled ? (
          <div className={classes.disabledDescription}>
            <div>{option.pricingDetails}</div>
            <div>
              <ErrorSmall aria-label={option.disabledReason} role="img" />{' '}
              {option.disabledReason}
            </div>
          </div>
        ) : (
          option.pricingDetails
        ),
        suffix,
        textOverflow: DropdownOptionTextOverflow.wrap,
        isSelectable: !option.disabled,
      });
      lastPaymentOptionType = option.type;
    }

    return results;
  };

  const options: DropdownOptionProps[] = getPaymentOptionsDropdownOptions(
    paymentOptions || [],
  );

  const onChange = (option: DropdownOptionProps) => {
    onSelect(option.id!);
  };

  return (
    <div
      className={st(classes.root, {
        isMobile,
        theme,
      })}
    >
      <Dropdown
        label={label}
        key={getServiceSlotIdentifier(service.nestedSlot)}
        data-hook={`${
          ServicePaymentDropdownHooks.Dropdown
        }_${getServiceSlotIdentifier(service.nestedSlot)}`}
        className={st(classes.dropdown, { theme })}
        options={options}
        theme={theme}
        initialSelectedId={selectedId}
        onChange={onChange}
      />
    </div>
  );
};
